import {ParserWebP, getParserWebP} from '@gongxigames/framework/core/fragments/WebP/ParserWebP';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {GLTexture, Renderer, BaseTexture, ISize, ALPHA_MODES, Resource, INSTALLED, TARGETS, FORMATS, TYPES} from 'pixi.js';

export type ResourceWebP = {
    type: 'WebP';
    binWebP: Uint8Array;
};

/**
 * Custom WebP resource loading plugin.
 * The code is mostly taken from PIXI ArrayResource
 */
export class BufferResourceWebP extends Resource {

    public static parserWebP: ParserWebP;

    data: ResourceWebP;

    public static test(source: any): source is ResourceWebP {
        return source.type === 'WebP' && source.binWebP instanceof Uint8Array;
    }

    public static init(): Promise<void> {
        return getParserWebP()
            .then(parser => {
                this.parserWebP = parser;
                INSTALLED.push(BufferResourceWebP);
            });
    }

    constructor(source: ResourceWebP, options: ISize) {
        const { width, height } = options || {};

        if (!width || !height) {
            throw new Error('BufferResource width or height invalid');
        }

        super(width, height);
        this.data = source;
    }

    /**
     * Uploading data directly to the video card
     */
    public upload(renderer: Renderer, baseTexture: BaseTexture, glTexture: GLTexture): boolean {
        const gl = renderer.gl;
        const data = this.decodeWebP();

        if (!data) {
            return false;
        }

        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, baseTexture.alphaMode === ALPHA_MODES.UNPACK);

        if (glTexture.width === data.width && glTexture.height === data.height) {
            gl.texSubImage2D(
                baseTexture.target ?? TARGETS.TEXTURE_2D,
                0,
                0,
                0,
                baseTexture.format ?? FORMATS.RGBA,
                baseTexture.type ?? TYPES.UNSIGNED_BYTE,
                data
            );
        } else {
            glTexture.width = data.width;
            glTexture.height = data.height;

            gl.texImage2D(
                baseTexture.target ?? TARGETS.TEXTURE_2D,
                0,
                glTexture.internalFormat,
                baseTexture.format ?? FORMATS.RGBA,
                glTexture.type,
                data
            );
        }

        return true;
    }

    public dispose(): void {
        (this as any).data = null;
    }

    private decodeWebP(): ImageData | undefined {
        for (let i = 0; i < 256; i++) {
            // INFO: CLFR-2203 iOS 15.4.1 webAssembly fail bug fix
            try {
                return BufferResourceWebP.parserWebP(this.data.binWebP);
            } catch (error) {
                consoleLogger.warn('WebAssembly WebP decoding fail');
            }
        }

        consoleLogger.error('Can\'t decode WebP image');
    }
}
